// Item heuristics (§10.3). Cheap checks run every tick (banana behind you, glove range, balloon
// emergencies); the bomb, plunger and Big Shake read the whole field and run on think ticks.
// Items are pressed with an aim vector from the bot to the aim point (the sim clamps ranges).
import { NO_SLOT, TUNE } from '../constants';
import { BS, EK, ItemKind } from '../ids';
import { DEG } from '../math';
import { MODES } from '../modes/index';
import { chance } from '../rng';
import { canUseItem } from '../sim/stateTable';
import type { Vec2 } from '../types';
import { Goal } from './brain';
import { setGoal } from './goals';
import { grounded, pow07, type Ctx } from './context';
import { hittable, isEnemy, pushDir } from './tactics';
import { BOT_TUNE } from './tuning';
import { dcos, dsin } from '../sim/dmath';

const I = TUNE.bot.items;
const F = BOT_TUNE.feel;
const P: Vec2 = { x: 0, y: 0 };
const BALL = { x: 0, y: 0, bx: 0, by: 0 };

function usable(ctx: Ctx): boolean {
  const b = ctx.self;
  if (b.item === ItemKind.NONE || ctx.w.tick < ctx.brain.itemNext) return false;
  if (!canUseItem(b.state, b.item)) return false;
  return !MODES[ctx.w.cfg.modeId].itemsBlocked(ctx.w, b.slot);
}

function use(ctx: Ctx, ax: number, ay: number): void {
  const br = ctx.brain;
  br.press.item = true;
  br.press.ax = ax;
  br.press.ay = ay;
  br.itemNext = ctx.w.tick + F.itemGapTicks;
  ctx.tele.itemPresses[ctx.self.item]++;
}

/** Exposure of a bean at (x, y) attacked from the bot (§10.2 formula, without weights). */
function exposureOf(ctx: Ctx, x: number, y: number): number {
  const b = ctx.self;
  const vr = ctx.field.voidRayDist(ctx.w, x, y, x - b.pos.x, y - b.pos.y);
  return TUNE.bot.attack.exposureBase + pow07(Math.max(0, Math.min(1, 1 - vr / TUNE.bot.rayMax)));
}

/** Per-tick item checks for a bean on its feet. */
export function itemReflexes(ctx: Ctx): void {
  if (!usable(ctx)) return;
  const b = ctx.self;
  const br = ctx.brain;
  switch (b.item) {
    case ItemKind.BANANA: {
      if (ctx.w.tick < br.bananaNext) return;
      const fx = dcos(b.facing);
      const fy = dsin(b.facing);
      const mySafety = ctx.field.safety(b.pos.x, b.pos.y);
      for (let s = 0; s < ctx.seen.length; s++) {
        if (s === b.slot || !isEnemy(ctx, s)) continue;
        const o = ctx.seen[s];
        if (!o.body) continue;
        const dx = o.x - b.pos.x;
        const dy = o.y - b.pos.y;
        const d = Math.hypot(dx, dy);
        if (d < 1e-6) continue;
        const behind = (dx * fx + dy * fy) / d < -F.bananaBehindCos;
        const chasing = (o.vx * -dx + o.vy * -dy) / d > F.bananaChaseSpeed;
        if ((behind && d < I.bananaBehindRange) || (mySafety < I.bananaChokeSafety && chasing && d < F.bananaChokeRange)) {
          use(ctx, 0, 0);
          br.bananaNext = ctx.w.tick + F.bananaGapTicks;
          return;
        }
      }
      return;
    }
    case ItemKind.GLOVE: {
      if (b.state !== BS.NORMAL) return;
      // Held a while: a person stops waiting for the perfect punch.
      const bored = ctx.w.tick - br.itemSince > F.itemBoredTicks;
      const alignCos = dcos((bored ? F.gloveBoredAlignDeg : I.gloveAlignDeg) * DEG);
      const needExposure = bored ? F.gloveBoredExposure : I.gloveExposure;
      for (let s = 0; s < ctx.seen.length; s++) {
        if (s === b.slot || !isEnemy(ctx, s) || !hittable(ctx, s)) continue;
        const o = ctx.seen[s];
        const dx = o.x - b.pos.x;
        const dy = o.y - b.pos.y;
        const d = Math.hypot(dx, dy);
        if (d > I.gloveRange || d < 1e-6) continue;
        const ts = pushDir(ctx, o.x, o.y, b.pos.x, b.pos.y, P);
        const aligned = (dx * P.x + dy * P.y) / d >= alignCos;
        const dizzy = ctx.w.beans[s].dizzy;
        const cornered = ctx.field.safety(b.pos.x, b.pos.y) < F.cowardCorneredSafety;
        if (br.p.rangedItems && !cornered) continue;
        if (aligned && (exposureOf(ctx, o.x, o.y) > needExposure || dizzy > I.gloveDizzy || ts < F.gloveEdgeSafety)) {
          use(ctx, dx, dy);
          return;
        }
      }
      return;
    }
    case ItemKind.BALLOON:
      balloonEmergency(ctx);
      return;
    default:
      return;
  }
}

/** Balloon (§10.3): kept for the auto-save; popped manually only when a hazard cannot be jumped. */
function balloonEmergency(ctx: Ctx): void {
  const b = ctx.self;
  const hz = ctx.hz;
  if (hz.armCount > 0 && !grounded(b) && b.vz < 0) {
    const t = hz.sweeperTicks(b.pos.x, b.pos.y, b.radius);
    const landIn = (b.z - b.groundZ) / Math.max(1e-3, -b.vz) * 60;
    if (t <= I.balloonHitTicks && t >= landIn) {
      use(ctx, 0, 0);
      return;
    }
  }
  if (hz.ballCount > 0) {
    const t = hz.ballTicks(b.pos.x, b.pos.y, b.radius, I.balloonHitTicks);
    if (t <= I.balloonHitTicks && hz.ballVelocity(b.pos.x, b.pos.y, b.radius, I.balloonHitTicks, BALL)) {
      // Trapped if neither side step gets out of the ball's path.
      const l = Math.max(1e-6, Math.hypot(BALL.x, BALL.y));
      const px = -BALL.y / l;
      const py = BALL.x / l;
      const out = 2 * (b.radius + F.balloonDodgeStep);
      const sa = ctx.field.safety(b.pos.x + px * out, b.pos.y + py * out);
      const sb = ctx.field.safety(b.pos.x - px * out, b.pos.y - py * out);
      if (sa < F.balloonTrappedSafety && sb < F.balloonTrappedSafety) use(ctx, 0, 0);
    }
  }
}

/** Think-time item decisions: bomb, plunger and Big Shake (§10.3). */
export function itemThink(ctx: Ctx): void {
  if (!usable(ctx)) return;
  const b = ctx.self;
  const br = ctx.brain;
  if (b.state !== BS.NORMAL) return;
  switch (b.item) {
    case ItemKind.BOMB:
      bombThink(ctx);
      return;
    case ItemKind.PLUNGER: {
      const max = TUNE.plunger.speed * TUNE.plunger.lifeTicks / 60;
      const minRange = br.p.rangedItems ? F.cowardPlungerMin : F.plungerMin;
      let best = NO_SLOT;
      let bs = 0;
      for (let s = 0; s < ctx.seen.length; s++) {
        if (s === b.slot || !isEnemy(ctx, s) || !hittable(ctx, s)) continue;
        const o = ctx.seen[s];
        const dx = o.x - b.pos.x;
        const dy = o.y - b.pos.y;
        const d = Math.hypot(dx, dy);
        if (d > Math.min(max, I.plungerRange) || d < minRange) continue;
        const ts = pushDir(ctx, o.x, o.y, b.pos.x, b.pos.y, P);
        const align = (dx * P.x + dy * P.y) / d;
        const danger = ctx.hz.danger(o.x - (dx / d) * F.plungerBackProbe, o.y - (dy / d) * F.plungerBackProbe, b.radius, true);
        // T's back toward the void (seen from us) or toward a hazard: yank it in, then shove it back out.
        const score = (align > F.plungerAlign && ts < F.plungerEdgeSafety ? align : 0) + danger;
        if (score > bs) {
          bs = score;
          best = s;
        }
      }
      if (best === NO_SLOT) return;
      const o = ctx.seen[best];
      const d = Math.hypot(o.x - b.pos.x, o.y - b.pos.y);
      const lead = br.p.lead * d / TUNE.plunger.speed;
      use(ctx, o.x + o.vx * lead - b.pos.x, o.y + o.vy * lead - b.pos.y);
      // Follow up: the yank brings the target into shove range with its back to the void.
      setGoal(ctx, Goal.ATTACK, best, 0, br.goalU);
      return;
    }
    case ItemKind.SHAKE: {
      let near = 0;
      for (let s = 0; s < ctx.seen.length; s++) {
        if (s === b.slot || !isEnemy(ctx, s) || !ctx.seen[s].body) continue;
        if (Math.hypot(ctx.seen[s].x - b.pos.x, ctx.seen[s].y - b.pos.y) < I.shakeRadius) near++;
      }
      let attacking = false;
      if (br.goal === Goal.ATTACK && br.target !== NO_SLOT) {
        const o = ctx.seen[br.target];
        attacking = Math.hypot(o.x - b.pos.x, o.y - b.pos.y) < I.shakeRadius && exposureOf(ctx, o.x, o.y) > I.shakeExposure;
      }
      if (near >= I.shakeEnemies || attacking) use(ctx, 0, 0);
      return;
    }
    default:
      return;
  }
}

/** Bomb (§10.3): the densest cluster of ≥ 2 enemies, or an exposed bean near an edge; led targets. */
function bombThink(ctx: Ctx): void {
  const b = ctx.self;
  const br = ctx.brain;
  const lead = I.bombLeadSec * br.p.lead;
  const minSelf = br.p.rangedItems ? Math.max(I.bombMinSelfDist, F.cowardBombMin) : I.bombMinSelfDist;
  let bestScore = 0;
  let bx = 0;
  let by = 0;
  for (let s = 0; s < ctx.seen.length; s++) {
    if (s === b.slot || !ctx.seen[s].body) continue;
    if (!isEnemy(ctx, s)) continue;
    const o = ctx.seen[s];
    const x = o.x + o.vx * lead;
    const y = o.y + o.vy * lead;
    const d = Math.hypot(x - b.pos.x, y - b.pos.y);
    if (d < minSelf || d > TUNE.bomb.maxRange) continue;
    let cluster = 0;
    let allies = 0;
    for (let k = 0; k < ctx.seen.length; k++) {
      if (!ctx.seen[k].body) continue;
      const q = ctx.seen[k];
      if (Math.hypot(q.x + q.vx * lead - x, q.y + q.vy * lead - y) > TUNE.bomb.radius) continue;
      if (k === b.slot) {
        cluster = -99;
        break;
      }
      if (isEnemy(ctx, k)) cluster++;
      else allies++;
    }
    if (allies > 0 && !br.p.crowdBombs) continue;
    const exposed = ctx.field.safety(x, y) < F.bombExposedSafety;
    let score = 0;
    if (cluster >= I.bombMinCluster) score = cluster + (br.p.crowdBombs ? F.chaosCrowdBonus : 0);
    else if (exposed && cluster >= 1) score = F.bombExposedScore;
    if (score > bestScore) {
      bestScore = score;
      bx = x;
      by = y;
    }
  }
  if (bestScore <= 0) return;
  // Land it on the floor: a target at the rim, plus a little aim error, put a third of the bombs in
  // the milk (a sploosh). Slide the landing point toward us until it has floor around it, as long
  // as the target stays well inside the blast.
  const dx = b.pos.x - bx;
  const dy = b.pos.y - by;
  const dl = Math.hypot(dx, dy);
  let k = 0;
  while (ctx.field.safety(bx, by) < F.bombLandSafety) {
    if (++k > F.bombLandSteps || dl < 1e-6) return;
    bx += (dx / dl) * F.bombLandStep;
    by += (dy / dl) * F.bombLandStep;
    if (Math.hypot(bx - b.pos.x, by - b.pos.y) < TUNE.bomb.minRange) return;
  }
  if (!chance(br, F.bombThrowChance)) return;
  use(ctx, bx - b.pos.x, by - b.pos.y);
}


// ---- Loose bombs (§3.3: "It can be grabbed and re-thrown") ----------------------------------------

/** BOMB_GRAB goal: walk up to a lit bomb with fuse to spare and grab it. */
export function bombGrab(ctx: Ctx): void {
  const br = ctx.brain;
  const b = ctx.self;
  let e = null as null | { pos: { x: number; y: number }; data: number; timer: number };
  for (const ent of ctx.w.ents) if (ent.id === br.targetEnt && ent.kind === EK.BOMB) e = ent;
  if (!e || e.data !== NO_SLOT || e.timer <= F.bombRegrabMinFuse * 0.6) {
    br.forceThink = true;
    br.want.mode = 0;
    return;
  }
  const dx = e.pos.x - b.pos.x;
  const dy = e.pos.y - b.pos.y;
  const d = Math.hypot(dx, dy);
  const reach = b.radius + TUNE.grab.reach + TUNE.bomb.bodyRadius;
  if (d < reach * 0.9 && b.cdGrab === 0 && b.state === BS.NORMAL && grounded(b)) {
    br.press.grab = true;
    br.press.sx = dx;
    br.press.sy = dy;
    ctx.tele.bombGrabs++;
    return;
  }
  br.want.mode = 1;
  br.want.px = e.pos.x;
  br.want.py = e.pos.y;
  br.want.speed = 1;
  br.want.arrive = 0.3;
  br.want.precise = true;
  br.want.margin = ctx.margin * 0.5;
  br.want.ignoreDanger = true;
}

/** CARRYING a bomb: throw it (6 m along the aim) where it hurts most, quickly. */
export function carryBomb(ctx: Ctx): void {
  const br = ctx.brain;
  const b = ctx.self;
  let fuse = 1e9;
  for (const e of ctx.w.ents) if (e.kind === EK.BOMB && e.data === b.slot) fuse = e.timer;
  const range = TUNE.bomb.rethrowRange;
  let best = -1;
  let bx = dcos(b.facing);
  let by = dsin(b.facing);
  const n = 16;
  for (let i = 0; i < n; i++) {
    const a = (i / n) * Math.PI * 2;
    const ux = dcos(a);
    const uy = dsin(a);
    const lx = b.pos.x + ux * range;
    const ly = b.pos.y + uy * range;
    let score = 0;
    for (let s = 0; s < ctx.seen.length; s++) {
      if (s === b.slot || !ctx.seen[s].body || !isEnemy(ctx, s)) continue;
      const o = ctx.seen[s];
      const dd = Math.hypot(o.x + o.vx * 0.6 - lx, o.y + o.vy * 0.6 - ly);
      if (dd < TUNE.bomb.radius) score += 1 + (TUNE.bomb.radius - dd) / TUNE.bomb.radius;
    }
    if (score > best) {
      best = score;
      bx = ux;
      by = uy;
    }
  }
  // Hold it a moment for a target to line up, never long: the fuse keeps burning in the hand.
  if (best > 0 || fuse < F.bombRegrabMinFuse || ctx.w.tick - br.carryTick > 40) {
    br.press.grab = true;
    br.press.ax = bx;
    br.press.ay = by;
    br.want.mode = 2;
    br.want.px = bx;
    br.want.py = by;
    br.want.speed = 0.3;
    ctx.tele.throws++;
  }
}
